/* eslint-disable react-refresh/only-export-components */
// Minimal drop-in for @tanstack/react-query.
// Implements the API surface used in this project:
//   useQuery, useMutation, useQueryClient, QueryClient, QueryClientProvider,
//   keepPreviousData, UseQueryResult (type).
//
// Saves ~9 kB gzip vs the full TanStack library.
// Design: module-level QueryClient singleton tracks a Map<string, CacheEntry>.
// Each useQuery subscriber re-renders when its cache entry changes.
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import {
  useQueryPolling,
  usePollingDocumentVisible,
} from "./use-query-polling";
import { buildQueryResult, resolvePlaceholder } from "./result";
import { isTransientQueryError, queryRetryDelayMs } from "./transient";
import { newEntry, type CacheEntry, type Subscriber } from "./cache-entry";
export {
  useMutation,
  type MutateCallOptions,
  type UseMutationResult,
} from "./mutation";

// ── Public types ──────────────────────────────────────────────────────────────

export type QueryKey = readonly unknown[];

export interface UseQueryResult<TData = unknown> {
  data: TData | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  isRefetching: boolean;
  /** True while `data` comes from placeholderData (e.g. keepPreviousData)
   *  instead of a settled fetch for the current key (TanStack v5 parity). */
  isPlaceholderData: boolean;
}

interface UseQueryOptions<TData> {
  queryKey: QueryKey;
  queryFn: (context: QueryFunctionContext) => Promise<TData>;
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  retry?: boolean | number | QueryRetryPredicate;
  retryDelay?: QueryRetryDelay;
  networkMode?: "online" | "always" | "offlineFirst";
  refetchInterval?: number | false;
  refetchIntervalInBackground?: boolean;
  refetchOnWindowFocus?: boolean;
  placeholderData?: ((prev: unknown) => unknown) | TData;
}

export type QueryFunctionContext = { readonly signal: AbortSignal };
export type QueryRetryPredicate = (
  failureCount: number,
  error: unknown,
) => boolean;
type QueryRetryOption = boolean | number | QueryRetryPredicate;
export type QueryRetryDelay = (failureCount: number, error: unknown) => number;

/** Sentinel used as placeholderData to keep previous data during loading. */
export const keepPreviousData = (prev: unknown): unknown => prev;

// ── Cache entry ───────────────────────────────────────────────────────────────
// Shape + factory live in cache-entry.ts (#217 item 3 split — see that file).

interface FetchPromiseInput<TData> {
  readonly cacheKey: string;
  readonly queryFn: (context: QueryFunctionContext) => Promise<TData>;
  readonly retry: QueryRetryOption | undefined;
  readonly retryCount: number;
  readonly writeSeqAtStart: number;
  readonly signal: AbortSignal;
}

interface FetchFailureInput<TData> extends FetchPromiseInput<TData> {
  readonly error: unknown;
  readonly promise: Promise<TData>;
}

function serializeKey(key: QueryKey): string {
  return JSON.stringify(key);
}

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "AbortError";

// ── QueryClient ───────────────────────────────────────────────────────────────

export interface QueryClientDefaultOptions {
  queries?: {
    staleTime?: number;
    gcTime?: number;
    retry?: boolean | number | QueryRetryPredicate;
    retryDelay?: QueryRetryDelay;
    networkMode?: "online" | "always" | "offlineFirst";
    refetchOnWindowFocus?: boolean;
  };
}

export class QueryClient {
  /** Exposed for test overrides. */
  readonly _cache = new Map<string, CacheEntry>();
  private readonly _defaults: QueryClientDefaultOptions;

  constructor(opts?: { defaultOptions?: QueryClientDefaultOptions }) {
    this._defaults = opts?.defaultOptions ?? {};
  }

  invalidateQueries({ queryKey }: { queryKey: QueryKey }): void {
    const prefix = JSON.stringify(queryKey).slice(0, -1); // strip trailing ]
    for (const [k, entry] of this._cache) {
      if (k.startsWith(prefix)) {
        entry.fetchedAt = 0; // mark stale
        entry.status = "idle";
        entry.invalidatedWhileFetching = entry.promise !== null;
        this._notify(entry);
      }
    }
  }

  resetQueries({ queryKey }: { queryKey: QueryKey }): void {
    const prefix = JSON.stringify(queryKey).slice(0, -1);
    for (const [k, entry] of this._cache) {
      if (k.startsWith(prefix)) this._resetEntryForClear(entry);
    }
  }

  setQueryData<TData>(
    queryKey: QueryKey,
    updater: TData | ((prev: TData | undefined) => TData),
  ): void {
    const k = serializeKey(queryKey);
    let entry = this._cache.get(k);
    if (!entry) {
      entry = newEntry();
      this._cache.set(k, entry);
    }
    const prev = entry.data as TData | undefined;
    entry.data =
      typeof updater === "function"
        ? (updater as (p: TData | undefined) => TData)(prev)
        : updater;
    entry.status = "success";
    entry.fetchedAt = Date.now();
    entry.writeSeq += 1;
    // Manual writes are authoritative — drop any pending mid-flight
    // invalidation mark (the writeSeq bump already discards that fetch).
    entry.invalidatedWhileFetching = false;
    this._notify(entry);
  }

  async fetchQuery<TData>({
    queryKey,
    queryFn,
    staleTime = 0,
  }: {
    queryKey: QueryKey;
    queryFn: (context: QueryFunctionContext) => Promise<TData>;
    staleTime?: number;
  }): Promise<TData> {
    const k = serializeKey(queryKey);
    const entry = this._cache.get(k);
    const isStale = !entry || Date.now() - entry.fetchedAt > staleTime;
    if (!isStale && entry?.status === "success" && entry.data !== undefined) {
      return entry.data as TData;
    }
    return this._runFetch<TData>(k, queryFn);
  }

  // Issue #174: fire-and-forget warmup used by usePrefetchSecondaryTabs.
  // Mirrors TanStack's prefetchQuery semantics — resolves to void even on
  // failure so the warmup loop can `await Promise.all([...])` without aborting
  // the rest of the prefetch batch when one endpoint flakes. Honours staleTime
  // by skipping the network call when the cache entry is still fresh.
  async prefetchQuery<TData>(options: {
    queryKey: QueryKey;
    queryFn: (context: QueryFunctionContext) => Promise<TData>;
    staleTime?: number;
  }): Promise<void> {
    try {
      await this.fetchQuery(options);
    } catch {
      // Warmup must never throw — useQuery will surface real errors when the
      // tab actually mounts.
    }
  }

  getQueryData<TData>(queryKey: QueryKey): TData | undefined {
    const k = serializeKey(queryKey);
    return this._cache.get(k)?.data as TData | undefined;
  }

  // TanStack v5 parity (admin cache audit): prefix-match every cached entry
  // under a partial key. Used to seed a detail view's placeholderData from a
  // row already present in a cached list page (list → detail navigation).
  getQueriesData<TData>({
    queryKey,
  }: {
    queryKey: QueryKey;
  }): Array<[string, TData | undefined]> {
    const prefix = serializeKey(queryKey).slice(0, -1); // strip trailing ]
    const matches: Array<[string, TData | undefined]> = [];
    for (const [k, entry] of this._cache) {
      if (!k.startsWith(prefix)) continue;
      matches.push([k, entry.data as TData | undefined]);
    }
    return matches;
  }

  clear(): void {
    // Entry objects own their subscriber sets, so dropping them from the map
    // would orphan every still-mounted hook: its next fetch builds a NEW
    // entry nobody listens to and the settle notifies zero subscribers —
    // same freeze class as the render→subscribe window (post-logout
    // RequireAuth hang). Reset subscribed entries in place; only unheard
    // ones actually leave the map.
    for (const [k, entry] of this._cache) {
      if (entry.subscribers.size > 0) {
        this._resetEntryForClear(entry);
        continue;
      }
      if (entry.refetchTimer) clearInterval(entry.refetchTimer);
      this._cache.delete(k);
    }
  }

  /** Wipe fetch state but keep subscribers (and their polling machinery —
   *  the mounted hook owns the timer and unregisters it on unmount). The
   *  writeSeq bump makes any in-flight settle lose ownership and go through
   *  _reconcileSupersededSettle instead of resurrecting pre-clear data. */
  private _resetEntryForClear(entry: CacheEntry): void {
    entry.abortController?.abort();
    entry.data = undefined;
    entry.error = null;
    entry.status = "idle";
    entry.fetchedAt = 0;
    entry.writeSeq += 1;
    entry.promise = null;
    entry.invalidatedWhileFetching = false;
    entry.abortController = null;
    this._notify(entry);
  }

  _subscribe(k: string, fn: Subscriber): () => void {
    const entry = this._ensureEntry(k);
    entry.subscribers.add(fn);
    return () => {
      entry.subscribers.delete(fn);
      if (entry.subscribers.size === 0) entry.abortController?.abort();
    };
  }

  _ensureEntry(k: string): CacheEntry {
    let entry = this._cache.get(k);
    if (!entry) {
      entry = newEntry();
      this._cache.set(k, entry);
    }
    return entry;
  }

  _notify(entry: CacheEntry): void {
    for (const sub of entry.subscribers) sub();
  }

  async _runFetch<TData>(
    k: string,
    queryFn: (context: QueryFunctionContext) => Promise<TData>,
    retryCount = 0,
    retry?: QueryRetryOption,
  ): Promise<TData> {
    let entry = this._cache.get(k);
    if (!entry) {
      entry = newEntry();
      this._cache.set(k, entry);
    }
    if (entry.promise) return entry.promise as Promise<TData>;

    const writeSeqAtStart = entry.writeSeq;
    const abortController = new AbortController();
    entry.status = "loading";
    entry.abortController = abortController;
    this._notify(entry);

    const p = this._createFetchPromise<TData>({
      cacheKey: k,
      queryFn,
      retry,
      retryCount,
      writeSeqAtStart,
      signal: abortController.signal,
    });
    entry.promise = p;
    return p;
  }

  _createFetchPromise<TData>(input: FetchPromiseInput<TData>): Promise<TData> {
    const p: Promise<TData> = input
      .queryFn({ signal: input.signal })
      .then((data) => {
        this._resolveFetchSuccess(
          input.cacheKey,
          p,
          data,
          input.writeSeqAtStart,
        );
        return data;
      })
      .catch(async (err: unknown) => {
        return this._resolveFetchFailure({ ...input, error: err, promise: p });
      });
    return p;
  }

  async _resolveFetchFailure<TData>(
    input: FetchFailureInput<TData>,
  ): Promise<TData> {
    const e = this._settledEntry(input.cacheKey, input.promise);
    if (isAbortError(input.error)) {
      this._resolveFetchAbort(e, input.writeSeqAtStart);
      throw input.error as Error;
    }
    if (e && this._shouldRetry(input) && e.writeSeq === input.writeSeqAtStart) {
      await delay(this._retryDelay(input));
      return this._runFetch<TData>(
        input.cacheKey,
        input.queryFn,
        input.retryCount + 1,
        input.retry,
      );
    }
    this._resolveFetchError(e, input.error, input.writeSeqAtStart);
    throw input.error as Error;
  }

  _resolveFetchSuccess<TData>(
    k: string,
    p: Promise<TData>,
    data: TData,
    writeSeqAtStart: number,
  ): void {
    const e = this._settledEntry(k, p);
    if (!e) return;
    if (e.writeSeq !== writeSeqAtStart) {
      this._reconcileSupersededSettle(e);
      return;
    }
    e.data = data;
    e.error = null;
    if (e.invalidatedWhileFetching) {
      // #217 item 3: the response was requested BEFORE the invalidation, so
      // render it (freshest snapshot we have) but keep the entry stale —
      // useStaleFetch sees idle+no-promise and refetches immediately.
      e.invalidatedWhileFetching = false;
      e.status = "idle";
      e.fetchedAt = 0;
    } else {
      e.status = "success";
      e.fetchedAt = Date.now();
    }
    this._notify(e);
  }

  _resolveFetchError(
    e: CacheEntry | undefined,
    err: unknown,
    writeSeqAtStart: number,
  ): void {
    if (!e) return;
    if (e.writeSeq !== writeSeqAtStart) {
      this._reconcileSupersededSettle(e);
      return;
    }
    e.error = err;
    e.status = "error";
    e.fetchedAt = 0;
    this._notify(e);
  }

  _resolveFetchAbort(e: CacheEntry | undefined, writeSeqAtStart: number): void {
    if (!e) return;
    if (e.writeSeq !== writeSeqAtStart) {
      this._reconcileSupersededSettle(e);
      return;
    }
    e.error = null;
    e.status = "idle";
    e.fetchedAt = 0;
    this._notify(e);
  }

  /** A settle that lost the writeSeq race (setQueryData / clear() mid-flight)
   *  no longer owns the entry — but _settledEntry already detached the
   *  promise, so returning silently here orphans it: subscribers keep the
   *  pre-settle snapshot and useStaleFetch (deps only refresh on a render)
   *  never re-runs. Always notify; when no fetch is left in flight, consume
   *  the invalidation mark (it referred to the settle that just lost) and
   *  demote a stranded "loading" to idle so the next effect pass refetches
   *  instead of spinning forever. */
  private _reconcileSupersededSettle(e: CacheEntry): void {
    if (!e.promise) {
      e.invalidatedWhileFetching = false;
      if (e.status === "loading") {
        e.status = "idle";
        e.fetchedAt = 0;
      }
    }
    this._notify(e);
  }

  _settledEntry<TData>(k: string, p: Promise<TData>): CacheEntry | undefined {
    const e = this._cache.get(k);
    if (e?.promise === p) {
      e.promise = null;
      e.abortController = null;
    }
    return e;
  }

  _getDefaults(): QueryClientDefaultOptions["queries"] {
    return this._defaults.queries;
  }

  private _shouldRetry(input: FetchFailureInput<unknown>): boolean {
    const retry = input.retry ?? this._defaults.queries?.retry ?? 1;
    if (typeof retry === "function")
      return retry(input.retryCount, input.error);
    if (retry === false) return false;
    if (retry === true) return input.retryCount < 3;
    return input.retryCount < retry;
  }

  private _retryDelay(input: FetchFailureInput<unknown>): number {
    const retryDelay = this._defaults.queries?.retryDelay;
    if (retryDelay) return retryDelay(input.retryCount, input.error);
    if (isTransientQueryError(input.error)) {
      return queryRetryDelayMs(input.retryCount);
    }
    return 0;
  }
}

// ── Context ───────────────────────────────────────────────────────────────────

const QueryClientCtx = createContext<QueryClient | null>(null);

interface QueryClientProviderProps {
  client: QueryClient;
  children?: ReactNode;
}

export function QueryClientProvider({
  client,
  children,
}: QueryClientProviderProps): React.JSX.Element {
  return (
    <QueryClientCtx.Provider value={client}>{children}</QueryClientCtx.Provider>
  );
}

export function useQueryClient(): QueryClient {
  const ctx = useContext(QueryClientCtx);
  if (!ctx)
    throw new Error("useQueryClient must be inside QueryClientProvider");
  return ctx;
}

export function useOptionalQueryClient(): QueryClient | null {
  return useContext(QueryClientCtx);
}

// ── useQuery ──────────────────────────────────────────────────────────────────

function noop(): void {}

function delay(ms: number): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function useStaleFetch(
  qc: QueryClient,
  k: string,
  opts: UseQueryOptions<unknown>,
  entry: CacheEntry | undefined,
): void {
  const queryFnRef = useRef(opts.queryFn);
  queryFnRef.current = opts.queryFn;
  const hasPromise = Boolean(entry?.promise);
  // Invalidations flip successful entries back to idle while keeping data,
  // matching TanStack's "stale but still render previous data" behavior.
  useEffect(() => {
    if (opts.enabled === false) return;
    const current = qc._cache.get(k);
    if (current?.status === "error" && opts.retry === false) return;
    if ((!current || current.status === "idle") && !current?.promise) {
      void qc
        ._runFetch(k, (context) => queryFnRef.current(context), 0, opts.retry)
        .catch(noop);
    }
  }, [
    qc,
    k,
    opts.enabled,
    opts.retry,
    entry?.status,
    entry?.writeSeq,
    entry?.fetchedAt,
    hasPromise,
  ]);
}

export function useQuery<TData = unknown>(
  opts: UseQueryOptions<TData>,
): UseQueryResult<TData> {
  const qc = useQueryClient();
  const k = serializeKey(opts.queryKey);
  const [, forceUpdate] = useState(0);
  const prevDataRef = useRef<TData | undefined>(undefined);
  const [isRefetching, setIsRefetching] = useState(false);

  // Subscribe to cache notifications. _notify only reaches CURRENT
  // subscribers and this subscription lands in a passive effect, so a fetch
  // settling between render and the effect flush (another consumer's request
  // resolving) would notify nobody and freeze this hook on the render-time
  // "loading" snapshot forever (blank screen / RequireAuth stuck isLoading).
  // The unconditional forceUpdate right after subscribing re-reads the entry
  // — the post-subscribe re-check useSyncExternalStore performs, hand-rolled
  // (costs 1 extra render per mount; deps are stable, so no loop).
  useEffect(() => {
    const unsubscribe = qc._subscribe(k, () => forceUpdate((n) => n + 1));
    forceUpdate((n) => n + 1);
    return unsubscribe;
  }, [qc, k]);
  const entryForEffects = qc._cache.get(k);
  useStaleFetch(qc, k, opts as UseQueryOptions<unknown>, entryForEffects);
  const pollingDocumentVisible = usePollingDocumentVisible(
    opts.refetchIntervalInBackground,
  );
  // Always call hook; guard is inside useQueryPolling (skips when falsy interval)
  useQueryPolling({
    enabled: opts.enabled !== false && pollingDocumentVisible,
    qc,
    k,
    refetchInterval: opts.refetchInterval,
    queryFn: opts.queryFn as (
      context: QueryFunctionContext,
    ) => Promise<unknown>,
  });

  const refetch = useCallback(async (): Promise<void> => {
    setIsRefetching(true);
    try {
      const entry = qc._cache.get(k);
      if (entry) entry.fetchedAt = 0; // force stale
      await qc._runFetch(
        k,
        opts.queryFn as (context: QueryFunctionContext) => Promise<unknown>,
        0,
        opts.retry,
      );
    } catch {
      /* errors captured in cache */
    } finally {
      setIsRefetching(false);
    }
  }, [qc, k]); // eslint-disable-line react-hooks/exhaustive-deps

  const entry = qc._cache.get(k);
  const displayData = resolvePlaceholder(
    entry?.data as TData | undefined,
    opts.placeholderData as
      | ((prev: TData | undefined) => TData | undefined)
      | TData
      | undefined,
    prevDataRef,
  );
  return buildQueryResult({
    entry,
    displayData,
    refetch,
    isRefetching,
    enabled: opts.enabled !== false,
  });
}
